import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/layout/AppHeader';
import { UserMenu } from '../components/layout/UserMenu';
import { FieldDetail, FieldsRegister, type FieldRow } from '../components/master/FieldsRegister';
import { MasterWellDetail, MasterWellsRegister, type MasterWellRow } from '../components/master/MasterWellsRegister';
import { PartiesRegister, PartyDetail, type PartyRow } from '../components/master/PartiesRegister';
import { TractDetail, TractsRegister, type TractRow } from '../components/master/TractsRegister';
import { BooksNav } from '../components/navigation/BooksNav';
import { ProductsRegister } from '../components/panels/ProductsRegister';
import { MASTER_API_URL } from '../constants';
import { hasPermission, permissionsOf } from '../domain/formatters';
import type { LoginResponse } from '../domain/types';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

interface BooksModulePageProps {
  auth: LoginResponse;
  onLogout: () => void | Promise<void>;
  authFetch: AuthFetch;
}

interface Paged<T> {
  items: T[];
  totalCount: number;
}

type MasterSection = 'parties' | 'fields' | 'tracts' | 'wells' | 'products' | 'new';

function parseMasterPath(pathname: string): { section: MasterSection; recordId?: string } {
  const parts = pathname.split('/').filter(Boolean);
  const section = (parts[1] ?? 'parties') as MasterSection;
  return { section, recordId: parts[2] };
}

async function readPaged<T>(res: Response): Promise<Paged<T>> {
  if (!res.ok) {
    const text = await res.text();
    try {
      const body = JSON.parse(text) as { message?: string };
      throw new Error(body.message || text || `Request failed (${res.status})`);
    } catch (e) {
      if (e instanceof Error && e.message !== text) {
        throw e;
      }
      throw new Error(text || `Request failed (${res.status})`);
    }
  }
  return (await res.json()) as Paged<T>;
}

export function BooksModulePage(props: BooksModulePageProps): ReactElement {
  const { auth, onLogout, authFetch } = props;
  const { pathname } = useLocation();
  const { recordId } = parseMasterPath(pathname);
  const detailFull = Boolean(recordId);

  const perms = permissionsOf(auth);
  const canWrite = hasPermission(perms, 'party:create', auth.role);
  const canReadProducts = hasPermission(perms, 'product:read', auth.role);

  if (pathname === '/master') {
    return <Navigate to="/master/parties" replace />;
  }

  if (pathname === '/master/new' && !canWrite) {
    return <Navigate to="/master/parties" replace />;
  }

  const moduleHome = pathname === '/commercial' || pathname === '/accounting';

  return (
    <div className={`page page-authed${detailFull ? ' page-authed--detail-full' : ''}`}>
      <AppHeader
        navSlot={<BooksNav />}
        endSlot={<UserMenu auth={auth} onLogout={onLogout} />}
      />
      <div className={`app-content${detailFull ? ' app-content--detail-full' : ''}`}>
        {moduleHome && <ModulePlaceholder path={pathname} />}
        {pathname.startsWith('/master') && (
          <MasterWorkspace
            authFetch={authFetch}
            pathname={pathname}
            canWrite={canWrite}
            canReadProducts={canReadProducts}
          />
        )}
      </div>
    </div>
  );
}

function ModulePlaceholder({ path }: { path: string }): ReactElement {
  const name = path.replace('/', '');
  return (
    <section>
      <h2>{name}</h2>
      <p>
        This module uses the same DDD write model as Master Data (aggregates, ports, PostgreSQL). The
        register UI ships in its type feature.
      </p>
      <p>
        Shared land and well identity lives in <Link to="/master/wells">Master</Link>.
      </p>
    </section>
  );
}

function tabClass(active: boolean): string {
  return active ? 'app-main-nav-link app-main-nav-link--active' : 'app-main-nav-link';
}

function MasterWorkspace({
  authFetch,
  pathname,
  canWrite,
  canReadProducts
}: {
  authFetch: AuthFetch;
  pathname: string;
  canWrite: boolean;
  canReadProducts: boolean;
}): ReactElement {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [parties, setParties] = useState<PartyRow[]>([]);
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [tracts, setTracts] = useState<TractRow[]>([]);
  const [wells, setWells] = useState<MasterWellRow[]>([]);

  const { section: tab, recordId } = parseMasterPath(pathname);

  const load = useCallback(async () => {
    setError('');
    const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
    const needed =
      tab === 'new'
        ? (['parties', 'fields', 'tracts', 'wells'] as const)
        : tab === 'products'
          ? []
          : tab === 'parties'
            ? (['parties'] as const)
            : tab === 'fields'
              ? (['fields'] as const)
              : tab === 'tracts'
                ? (['tracts'] as const)
                : (['wells'] as const);

    const results = await Promise.all(
      needed.map(async (entity) => {
        try {
          if (entity === 'parties') {
            return { entity, data: await readPaged<PartyRow>(await authFetch(`${MASTER_API_URL}/parties${qs}`)) };
          }
          if (entity === 'fields') {
            return { entity, data: await readPaged<FieldRow>(await authFetch(`${MASTER_API_URL}/fields${qs}`)) };
          }
          if (entity === 'tracts') {
            return { entity, data: await readPaged<TractRow>(await authFetch(`${MASTER_API_URL}/tracts${qs}`)) };
          }
          return { entity, data: await readPaged<MasterWellRow>(await authFetch(`${MASTER_API_URL}/wells${qs}`)) };
        } catch (e) {
          return { entity, error: e instanceof Error ? e.message : 'Search failed' };
        }
      })
    );

    let message = '';
    for (const row of results) {
      if ('error' in row && typeof row.error === 'string') {
        message = row.error;
        break;
      }
    }
    setError(message);
    for (const row of results) {
      if (!('data' in row) || row.data === undefined) {
        continue;
      }
      if (row.entity === 'parties') {
        setParties((row.data as Paged<PartyRow>).items);
      } else if (row.entity === 'fields') {
        setFields((row.data as Paged<FieldRow>).items);
      } else if (row.entity === 'tracts') {
        setTracts((row.data as Paged<TractRow>).items);
      } else if (row.entity === 'wells') {
        setWells((row.data as Paged<MasterWellRow>).items);
      }
    }
  }, [authFetch, q, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (section: MasterSection, id: string) => navigate(`/master/${section}/${id}`);

  if (recordId) {
    const party = parties.find((p) => p.id === recordId);
    const field = fields.find((f) => f.id === recordId);
    const tract = tracts.find((t) => t.id === recordId);
    const well = wells.find((w) => w.id === recordId);
    return (
      <div className="detail-page-shell">
        {tab === 'parties' && party && (
          <PartyDetail item={party} variant="page" authFetch={authFetch} onBack={() => navigate('/master/parties')} />
        )}
        {tab === 'fields' && field && (
          <FieldDetail item={field} variant="page" authFetch={authFetch} onBack={() => navigate('/master/fields')} />
        )}
        {tab === 'tracts' && tract && (
          <TractDetail item={tract} variant="page" authFetch={authFetch} onBack={() => navigate('/master/tracts')} />
        )}
        {tab === 'wells' && well && (
          <MasterWellDetail item={well} variant="page" authFetch={authFetch} onBack={() => navigate('/master/wells')} />
        )}
        {error && <p className="error">{error}</p>}
        {!error &&
          ((tab === 'parties' && !party) ||
            (tab === 'fields' && !field) ||
            (tab === 'tracts' && !tract) ||
            (tab === 'wells' && !well)) && <p className="detail-page-loading">Loading…</p>}
      </div>
    );
  }

  return (
    <section>
      <h2>Master data</h2>
      <p>Used by Contracts, Commercial, and Accounting. Procurement wells stay on /wells.</p>
      <nav className="app-main-nav" aria-label="Master records">
        <Link to="/master/parties" className={tabClass(tab === 'parties')}>
          Parties
        </Link>
        <Link to="/master/fields" className={tabClass(tab === 'fields')}>
          Fields
        </Link>
        <Link to="/master/tracts" className={tabClass(tab === 'tracts')}>
          Tracts
        </Link>
        <Link to="/master/wells" className={tabClass(tab === 'wells')}>
          Wells
        </Link>
        {canReadProducts && (
          <Link to="/master/products" className={tabClass(tab === 'products')}>
            Products
          </Link>
        )}
        {canWrite && (
          <Link to="/master/new" className={tabClass(tab === 'new')}>
            Create
          </Link>
        )}
      </nav>
      {error && tab !== 'products' && <p className="error">{error}</p>}
      {tab === 'products' && <ProductsRegister authFetch={authFetch} />}
      {tab === 'parties' && (
        <PartiesRegister
          rows={parties}
          query={q}
          onQuery={setQ}
          onOpen={(id) => open('parties', id)}
          authFetch={authFetch}
        />
      )}
      {tab === 'fields' && (
        <FieldsRegister
          rows={fields}
          query={q}
          onQuery={setQ}
          onOpen={(id) => open('fields', id)}
          authFetch={authFetch}
        />
      )}
      {tab === 'tracts' && (
        <TractsRegister
          rows={tracts}
          query={q}
          onQuery={setQ}
          onOpen={(id) => open('tracts', id)}
          authFetch={authFetch}
        />
      )}
      {tab === 'wells' && (
        <MasterWellsRegister
          rows={wells}
          query={q}
          onQuery={setQ}
          onOpen={(id) => open('wells', id)}
          authFetch={authFetch}
        />
      )}
      {tab === 'new' && canWrite && (
        <MasterCreateForms
          authFetch={authFetch}
          parties={parties}
          fields={fields}
          tracts={tracts}
          onCreated={() => {
            void load();
            navigate('/master/wells');
          }}
        />
      )}
    </section>
  );
}

function MasterCreateForms({
  authFetch,
  parties,
  fields,
  tracts,
  onCreated
}: {
  authFetch: AuthFetch;
  parties: PartyRow[];
  fields: FieldRow[];
  tracts: TractRow[];
  onCreated: () => void;
}): ReactElement {
  const [error, setError] = useState('');
  const [partyName, setPartyName] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [fieldState, setFieldState] = useState('TX');
  const [fieldRegion, setFieldRegion] = useState('Permian');
  const [tractName, setTractName] = useState('');
  const [tractState, setTractState] = useState('TX');
  const [tractCounty, setTractCounty] = useState('');
  const [surveySystem, setSurveySystem] = useState('Texas');
  const [abstractNo, setAbstractNo] = useState('');
  const [meridian, setMeridian] = useState('Indian');
  const [township, setTownship] = useState('');
  const [range, setRange] = useState('');
  const [section, setSection] = useState('');
  const [api, setApi] = useState('');
  const [wellName, setWellName] = useState('');
  const [wellState, setWellState] = useState('TX');
  const [wellCounty, setWellCounty] = useState('');
  const [wellRegion, setWellRegion] = useState('Permian');
  const [fieldId, setFieldId] = useState('');
  const [operatorId, setOperatorId] = useState('');
  const [tractId, setTractId] = useState('');
  const [idScheme, setIdScheme] = useState('TxRrcOilLease');
  const [idValue, setIdValue] = useState('');
  const [idJurisdiction, setIdJurisdiction] = useState('TX');

  async function post(path: string, body: unknown): Promise<void> {
    setError('');
    const res = await authFetch(`${MASTER_API_URL}${path}`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
    if (res.status === 403) {
      throw new Error('You cannot create master records.');
    }
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Save failed (${res.status})`);
    }
  }

  return (
    <div className="grid">
      {error && <p className="error">{error}</p>}
      <section className="card">
        <h3 className="form-section-title">Party</h3>
        <form
          className="grid"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await post('/parties', {
                legalName: partyName,
                kind: 'Company',
                roles: ['Lessor']
              });
              onCreated();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Save failed');
            }
          }}
        >
          <label>
            Legal name
            <input required value={partyName} onChange={(e) => setPartyName(e.target.value)} />
          </label>
          <div className="row">
            <button type="submit">Save party</button>
          </div>
        </form>
      </section>
      <section className="card">
        <h3 className="form-section-title">Field</h3>
        <form
          className="grid"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await post('/fields', {
                name: fieldName,
                state: fieldState,
                operatingRegion: fieldRegion,
                recoveryType: 'Primary'
              });
              onCreated();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Save failed');
            }
          }}
        >
          <label>
            Field name
            <input required value={fieldName} onChange={(e) => setFieldName(e.target.value)} />
          </label>
          <label>
            State
            <input value={fieldState} onChange={(e) => setFieldState(e.target.value)} maxLength={2} />
          </label>
          <label>
            Region
            <select value={fieldRegion} onChange={(e) => setFieldRegion(e.target.value)}>
              <option>Permian</option>
              <option>MidContinent</option>
              <option>Rockies</option>
              <option>IllinoisBasin</option>
            </select>
          </label>
          <div className="row">
            <button type="submit">Save field</button>
          </div>
        </form>
      </section>
      <section className="card">
        <h3 className="form-section-title">Tract</h3>
        <form
          className="grid"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await post('/tracts', {
                name: tractName,
                state: tractState,
                county: tractCounty,
                surveySystem,
                abstract: abstractNo,
                meridian,
                township,
                range,
                section
              });
              onCreated();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Save failed');
            }
          }}
        >
          <label>
            Tract name
            <input required value={tractName} onChange={(e) => setTractName(e.target.value)} />
          </label>
          <label>
            State
            <input value={tractState} onChange={(e) => setTractState(e.target.value)} maxLength={2} />
          </label>
          <label>
            County
            <input required value={tractCounty} onChange={(e) => setTractCounty(e.target.value)} />
          </label>
          <label>
            Survey
            <select value={surveySystem} onChange={(e) => setSurveySystem(e.target.value)}>
              <option>Texas</option>
              <option>Plss</option>
            </select>
          </label>
          {surveySystem === 'Texas' ? (
            <label>
              Abstract
              <input value={abstractNo} onChange={(e) => setAbstractNo(e.target.value)} />
            </label>
          ) : (
            <>
              <label>
                Meridian
                <input value={meridian} onChange={(e) => setMeridian(e.target.value)} />
              </label>
              <label>
                Township
                <input value={township} onChange={(e) => setTownship(e.target.value)} />
              </label>
              <label>
                Range
                <input value={range} onChange={(e) => setRange(e.target.value)} />
              </label>
              <label>
                Section
                <input value={section} onChange={(e) => setSection(e.target.value)} />
              </label>
            </>
          )}
          <div className="row">
            <button type="submit">Save tract</button>
          </div>
        </form>
      </section>
      <section className="card">
        <h3 className="form-section-title">Well</h3>
        <form
          className="grid"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await post('/wells', {
                apiNumber: api,
                name: wellName,
                wellType: 'Oil',
                wellStatus: 'Producing',
                fieldId,
                operatorPartyId: operatorId,
                state: wellState,
                county: wellCounty,
                operatingRegion: wellRegion,
                identifiers: idValue
                  ? [{ scheme: idScheme, value: idValue, jurisdiction: idJurisdiction }]
                  : [],
                tractIds: tractId ? [tractId] : []
              });
              onCreated();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Save failed');
            }
          }}
        >
          <label>
            API
            <input required value={api} onChange={(e) => setApi(e.target.value)} placeholder="10 or 14 digits" />
          </label>
          <label>
            Well name
            <input required value={wellName} onChange={(e) => setWellName(e.target.value)} />
          </label>
          <label>
            State
            <input value={wellState} onChange={(e) => setWellState(e.target.value)} maxLength={2} />
          </label>
          <label>
            County
            <input required value={wellCounty} onChange={(e) => setWellCounty(e.target.value)} />
          </label>
          <label>
            Region
            <select value={wellRegion} onChange={(e) => setWellRegion(e.target.value)}>
              <option>Permian</option>
              <option>MidContinent</option>
              <option>Rockies</option>
              <option>IllinoisBasin</option>
            </select>
          </label>
          <label>
            Field
            <select required value={fieldId} onChange={(e) => setFieldId(e.target.value)}>
              <option value="">Select field</option>
              {fields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Operator
            <select required value={operatorId} onChange={(e) => setOperatorId(e.target.value)}>
              <option value="">Select operator</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.legalName}
                </option>
              ))}
            </select>
          </label>
          <label>
            Tract
            <select value={tractId} onChange={(e) => setTractId(e.target.value)}>
              <option value="">Optional</option>
              {tracts.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Identifier
            <select value={idScheme} onChange={(e) => setIdScheme(e.target.value)}>
              <option>TxRrcOilLease</option>
              <option>OkOtc</option>
              <option>CogccFacility</option>
              <option>CogccLocation</option>
              <option>BlmSerial</option>
            </select>
          </label>
          <label>
            Identifier value
            <input value={idValue} onChange={(e) => setIdValue(e.target.value)} />
          </label>
          <label>
            Jurisdiction
            <input
              value={idJurisdiction}
              onChange={(e) => setIdJurisdiction(e.target.value)}
              placeholder="TX / OK / CO"
              maxLength={3}
            />
          </label>
          <div className="row">
            <button type="submit">Save well</button>
          </div>
        </form>
      </section>
    </div>
  );
}
